import { useRef, type HTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { usePresence, type PresenceState } from '../../hooks/useFloating';
import { Portal } from '../Portal/Portal';
import { useScrollLock } from './useScrollLock';

export interface ModalOverlayProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  open: boolean;
  /** Called when the backdrop itself is clicked (press and release both on the backdrop). */
  onDismiss?: () => void;
  closeOnOverlayClick?: boolean;
  /** Lock body scrolling while the overlay is open (default true). */
  lockScroll?: boolean;
  /** Receives the presence state so the panel can drive its own enter/exit. */
  children: (state: PresenceState) => ReactNode;
}

/**
 * Portalled modal scrim shared by Dialog, Drawer and CommandPalette.
 *
 * It owns nothing but presence, the dimmed backdrop (`zo-backdrop`), scroll lock and
 * backdrop dismissal — the same `usePresence` the floating primitive uses, so a modal fades out
 * instead of vanishing and unmounts synchronously when motion is off.
 */
export function ModalOverlay({
  open,
  onDismiss,
  closeOnOverlayClick = true,
  lockScroll = true,
  className,
  children,
  onMouseDown,
  onClick,
  ...rest
}: ModalOverlayProps) {
  const { mounted, state } = usePresence(open);
  // A drag that starts inside the panel and ends on the backdrop must not dismiss.
  const pressStartedInside = useRef(false);
  useScrollLock(open && lockScroll);

  if (!mounted) return null;

  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    onMouseDown?.(e);
    pressStartedInside.current = e.target !== e.currentTarget;
  };
  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    onClick?.(e);
    if (closeOnOverlayClick && !pressStartedInside.current && e.target === e.currentTarget) onDismiss?.();
    pressStartedInside.current = false;
  };

  return (
    <Portal>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        data-state={state}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        className={cn('zo-backdrop fixed inset-0 flex', className)}
        style={{ zIndex: 'var(--zo-z-overlay)' }}
        {...rest}
      >
        {children(state)}
      </div>
    </Portal>
  );
}
